import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import TrustBadges from "@/components/TrustBadges";
import TypewriterText from "@/components/TypewriterText";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import FloatingShapes from "@/components/effects/FloatingShapes";
import { GradientText } from "@/components/ui/gradient-text";
import { useEffect, useState } from "react";

const Hero = () => {
  const { ref: heroRef, isInView } = useScrollAnimation({ threshold: 0.1 });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  const benefits = [
    "Rank on Google & Google Maps",
    "Websites that turn visitors into calls",
    "Hands-on support from a real Michigan team"
  ];

  const typewriterWords = ["Advertising", "Website Design", "SEO"];

  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      {/* Premium floating shapes */}
      <FloatingShapes variant="hero" />
      
      {/* Subtle gradient overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80"
      />

      {/* Animated grid pattern - subtle tech feel */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="py-20 md:py-28 lg:py-36" ref={heroRef}>
          <div className={`max-w-4xl mx-auto text-center transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Trust badges above headline */}
            <div 
              className={`mb-8 transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '100ms' }}
            >
              <TrustBadges variant="dark" />
            </div>

            {/* Main headline with gradient accent */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Digital Growth in{" "}
              <GradientText variant="accent" className="inline-block pb-2">
                {prefersReducedMotion ? (
                  typewriterWords[0]
                ) : (
                  <TypewriterText words={typewriterWords} typingSpeed={100} deletingSpeed={50} pauseDuration={2000} />
                )}
              </GradientText>
            </h1>

            {/* Subheadline */}
            <p 
              className={`text-lg md:text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto transition-all duration-500 leading-relaxed ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '200ms' }}
            >
              We help Michigan contractors, service businesses, and local companies dominate local search, win more inbound calls, and build modern websites that convert.
            </p>

            {/* Benefits list with staggered animation */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-2 text-sm text-primary-foreground/80 transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <CheckCircle className="h-4 w-4 text-accent" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            {/* Dual CTAs with direct phone & quote */}
            <div 
              className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-4 transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '600ms' }}
            >
              <Button
                size="lg"
                variant="glow"
                asChild
                className="min-w-[220px] text-base group"
              >
                <a href="tel:+19898432600" className="flex items-center justify-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>Call (989) 843-2600</span>
                </a>
              </Button>
              <Button
                size="lg"
                variant="glass"
                asChild
                className="min-w-[200px] text-base text-primary-foreground border-primary-foreground/30 hover:border-accent group"
              >
                <Link to="/contact" className="flex items-center justify-center gap-2">
                  <span>Get a Free Quote</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
            <p className="text-xs text-primary-foreground/60 tracking-wide">
              Serving Huron, Tuscola, Sanilac Counties &amp; All of Michigan &bull; Fast Response &bull; Direct Phone Access
            </p>
          </div>
        </div>
      </div>

      {/* Clean gradient transition - no wave */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default Hero;
